import { useCallback, useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import AppNavbar from "./AppNavbar.js";
import SectionCard from "./SectionCard.js";
import {
  buildLoginUrl,
  buildLogoutUrl,
  buildSignupUrl,
  getTokenStorageKey,
  isTokenExpired,
  readTokenFromUrlHash,
} from "./auth.js";
import { catalogFetch } from "./catalogApi.js";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || "";
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || "";
const COGNITO_REDIRECT_URI = `${window.location.origin}/market-resolution`;
const COGNITO_LOGOUT_URI = `${window.location.origin}/market-resolution`;

function MarketResolutionAdminPage() {
  const [idToken, setIdToken] = useState("");
  const [me, setMe] = useState(null);
  const [balance, setBalance] = useState({ cash_available: "0.00", cash_reserved: "0.00" });
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [marketId, setMarketId] = useState("");
  const [outcome, setOutcome] = useState("YES");
  const [requestId, setRequestId] = useState(`resolve-${Date.now()}`);
  const [resolvedAt, setResolvedAt] = useState(String(Math.floor(Date.now() / 1000)));

  useEffect(() => {
    const tokenFromHash = readTokenFromUrlHash();
    const storedToken = localStorage.getItem(getTokenStorageKey()) || "";
    const token = tokenFromHash || storedToken;

    if (!token || isTokenExpired(token)) {
      localStorage.removeItem(getTokenStorageKey());
      setIdToken("");
      return;
    }

    localStorage.setItem(getTokenStorageKey(), token);
    setIdToken(token);
    if (tokenFromHash) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loginUrl = useMemo(
    () => buildLoginUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const signupUrl = useMemo(
    () => buildSignupUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const logoutUrl = useMemo(
    () => buildLogoutUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_LOGOUT_URI),
    []
  );

  const apiFetch = useCallback(
    async (path, options = {}) => {
      if (!API_BASE_URL) {
        throw new Error("Missing REACT_APP_API_BASE_URL");
      }
      if (!idToken) {
        throw new Error("Not signed in");
      }
      const response = await fetch(`${API_BASE_URL}${path}`, {
        method: options.method || "GET",
        body: options.body,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
          ...(options.headers || {}),
        },
      });
      const body = await response.json();
      if (!response.ok) {
        throw new Error(body.error || body.message || "Request failed");
      }
      return body;
    },
    [idToken]
  );

  useEffect(() => {
    let cancelled = false;

    const loadAccount = async () => {
      if (!idToken) {
        setMe(null);
        setBalance({ cash_available: "0.00", cash_reserved: "0.00" });
        setMarkets([]);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const [meRes, balanceRes, myMarketsRes] = await Promise.all([
          apiFetch("/me"),
          apiFetch("/me/balance"),
          catalogFetch("/me/markets", idToken),
        ]);
        if (!cancelled) {
          setMe(meRes);
          setBalance(balanceRes);
          const items = Array.isArray(myMarketsRes.items) ? myMarketsRes.items : [];
          const validItems = items.filter((item) => item && item.market_id);
          setMarkets(validItems);
          setMarketId((current) => {
            if (current && validItems.some((item) => item.market_id === current)) {
              return current;
            }
            return validItems[0]?.market_id || "";
          });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load account");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAccount();
    return () => {
      cancelled = true;
    };
  }, [idToken, apiFetch]);

  const handleLogin = () => {
    if (!loginUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(loginUrl);
  };

  const handleSignup = () => {
    if (!signupUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(signupUrl);
  };

  const handleLogout = () => {
    localStorage.removeItem(getTokenStorageKey());
    setIdToken("");
    setMe(null);
    setBalance({ cash_available: "0.00", cash_reserved: "0.00" });
    setMarkets([]);
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!marketId.trim()) {
      setError("market_id is required");
      return;
    }

    const resolvedAtValue = Number(resolvedAt);
    if (!Number.isInteger(resolvedAtValue) || resolvedAtValue <= 0) {
      setError("resolved_at must be a positive UNIX timestamp");
      return;
    }

    const message = {
      message_type: "MARKET.RESOLVE",
      request_id: requestId.trim(),
      market_id: marketId.trim(),
      outcome,
      resolved_at: resolvedAtValue,
    };
    if (outcome === "VOID") {
      message.void = true;
    }

    setSubmitting(true);
    try {
      const result = await apiFetch("/market-resolution", {
        method: "POST",
        body: JSON.stringify(message),
      });
      setSuccessMessage(
        `Sent market resolution request for ${result.message?.market_id || marketId.trim()}.`
      );
      setRequestId(`resolve-${Date.now()}`);
      setMarkets((current) =>
        current.map((market) =>
          market.market_id === marketId.trim()
            ? {
                ...market,
                status: outcome === "VOID" ? "VOIDED" : "RESOLVED",
              }
            : market
        )
      );
    } catch (err) {
      setError(err?.message || "Failed to send message");
    } finally {
      setSubmitting(false);
    }
  };

  const availableMarkets = useMemo(
    () => markets.filter((market) => String(market.status || "").toUpperCase() === "OPEN"),
    [markets]
  );

  return (
    <div className="App">
      <AppNavbar
        links={[
          { href: "/", label: "Home" },
          { href: "/open-orders", label: "Open Orders" },
          { href: "/leaderboard", label: "Leaderboard" },
          { href: "/market-resolution", label: "Resolve Market" },
          { href: "/markets/new", label: "Create Market" },
        ]}
        isSignedIn={Boolean(idToken)}
        me={me}
        balance={balance}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onSignup={handleSignup}
      />
      <Container className="py-4">
        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" className="mb-0">{error}</Alert>
            </Col>
          </Row>
        )}
        {successMessage && (
          <Row className="mb-3">
            <Col>
              <Alert variant="success" className="mb-0">{successMessage}</Alert>
            </Col>
          </Row>
        )}
        {!idToken ? (
          <Row>
            <Col>
              <Alert variant="info" className="mb-0">
                Sign in to access market resolution tooling.
              </Alert>
            </Col>
          </Row>
        ) : availableMarkets.length === 0 ? (
          <Row>
            <Col>
              <Alert variant="warning" className="mb-0">
                You do not have any open markets available to resolve yet.
              </Alert>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col lg={8}>
              <SectionCard title="Resolve Your Market">
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>market</Form.Label>
                    <Form.Select
                      value={marketId}
                      onChange={(e) => setMarketId(e.target.value)}
                      disabled={submitting || loading}
                    >
                      {availableMarkets.map((market) => (
                        <option key={market.market_id} value={market.market_id}>
                          {market.title} ({market.market_id})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>request_id</Form.Label>
                    <Form.Control
                      type="text"
                      value={requestId}
                      onChange={(e) => setRequestId(e.target.value)}
                      disabled={submitting || loading}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>outcome</Form.Label>
                    <Form.Select
                      value={outcome}
                      onChange={(e) => setOutcome(e.target.value)}
                      disabled={submitting || loading}
                    >
                      <option value="YES">YES</option>
                      <option value="NO">NO</option>
                      <option value="VOID">VOID</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>resolved_at (UNIX seconds)</Form.Label>
                    <Form.Control
                      type="number"
                      value={resolvedAt}
                      onChange={(e) => setResolvedAt(e.target.value)}
                      disabled={submitting || loading}
                    />
                  </Form.Group>

                  <Button type="submit" disabled={submitting || loading}>
                    {submitting ? "Sending..." : "Resolve Market"}
                  </Button>
                </Form>
              </SectionCard>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}

export default MarketResolutionAdminPage;
